/**
 * The find panel's logic (decision 41; part 19a log, table F, principle 6): the query and its options, searches run in
 * the find worker as the writer types (F2) and edits (F9), moving from match to match across chapters (F3, F4), replace
 * (F5), replace-all and its book-wide undo (F6, F7, tables L and U, through the workspace). The workspace, the worker and
 * the timers are injected, so the logic runs in tests; the state is Solid signals, so the panel follows it.
 */
import type { FindQuery, QueryProblem } from '@cockatrice/core';
import { createSignal } from 'solid-js';
import type { Accessor, Setter } from 'solid-js';
import type { EditorHandle, FindScope, FindTexts, ReplaceAllOutcome, ReplaceComputation, UndoReplaceOutcome } from '../shell/workspace.ts';
import type { FindOutcome, FindRequest } from './host.ts';
import type { FindChapter } from './protocol.ts';

/** F2, F9: how long after the last change a search runs, in milliseconds. */
export const SEARCH_DELAY_MS = 250;

/** What the controller needs of the workspace. */
export interface FindPlace {
  readonly bookId: () => string | null;
  readonly chapterId: () => string | null;
  readonly editor: () => EditorHandle | null;
  readonly editable: () => boolean;
  readonly texts: (scope: FindScope) => Promise<FindTexts | null>;
  readonly openChapter: (chapterId: string) => Promise<void>;
  readonly replaceAll: (scope: FindScope, compute: (chapters: readonly FindChapter[]) => Promise<ReplaceComputation>) => Promise<ReplaceAllOutcome>;
  readonly undoReplaceAll: () => Promise<UndoReplaceOutcome>;
}

/** What the controller needs; everything with an effect is injected. */
export interface FindControllerOptions {
  readonly place: FindPlace;
  /** Runs a job in the find worker (`FindHost.run`). */
  readonly run: (request: FindRequest) => Promise<FindOutcome>;
  /** Runs `run` after `ms`; returns what cancels it. */
  readonly schedule: (ms: number, run: () => void) => () => void;
}

/** The last search's results: the counts per chapter searched, in order. */
export interface FindResults {
  readonly bookId: string;
  readonly scope: FindScope;
  readonly chapters: readonly string[];
  readonly counts: readonly number[];
  readonly unreadable: number;
}

/** What the panel says about the search: nothing to search, under way, its results, no pattern, stopped (T1), failed (T4). */
export type FindStatus =
  | { readonly kind: 'idle' }
  | { readonly kind: 'searching' }
  | { readonly kind: 'found'; readonly results: FindResults }
  | { readonly kind: 'refused'; readonly problem: QueryProblem }
  | { readonly kind: 'stopped' }
  | { readonly kind: 'failed' };

/** Where the current match stands among the open chapter's: its place from 1, of how many; null with none current. */
export interface FindPosition {
  readonly index: number;
  readonly of: number;
}

/** What the last replace or undo did, for the panel to say (F5–F7). */
export type FindNews =
  | { readonly kind: 'replacing' }
  | { readonly kind: 'replaced-all'; readonly outcome: ReplaceAllOutcome }
  | { readonly kind: 'undone'; readonly outcome: UndoReplaceOutcome }
  | { readonly kind: 'left-one' };

/** A Solid signal as a pair. */
interface Signal<T> {
  readonly get: Accessor<T>;
  readonly set: Setter<T>;
}

function signal<T>(value: T): Signal<T> {
  const [get, set] = createSignal(value, { equals: false });
  return { get, set };
}

/** The search's request: every chapter in scope, or the open chapter alone (F9, F10). */
type Wanted = 'all' | 'open';

/** Table F: the find panel. */
export class FindController {
  readonly #options: FindControllerOptions;
  readonly #open = signal(false);
  readonly #query = signal<FindQuery>({ text: '', matchCase: false, wholeWords: false, regex: false });
  readonly #replacement = signal('');
  readonly #scope = signal<FindScope>('book');
  readonly #status = signal<FindStatus>({ kind: 'idle' });
  readonly #position = signal<FindPosition | null>(null);
  readonly #news = signal<FindNews | null>(null);
  readonly #busy = signal(false);
  /** T5: the last search started; an answer to any other is dropped. */
  #searches = 0;
  /** The moves and replaces asked for, run one after another in the order asked (a held Enter, Next pressed twice). */
  #actions: Promise<unknown> = Promise.resolve();
  /** The search waiting to run, and what cancels it. */
  #pending: { readonly wanted: Wanted; readonly cancel: () => void } | null = null;

  constructor(options: FindControllerOptions) {
    this.#options = options;
  }

  /** Whether the panel is open. */
  readonly open = (): boolean => this.#open.get();
  readonly query = (): FindQuery => this.#query.get();
  readonly replacement = (): string => this.#replacement.get();
  readonly scope = (): FindScope => this.#scope.get();
  readonly status = (): FindStatus => this.#status.get();
  /** The current match's place in the open chapter, as of the last move or search. */
  readonly position = (): FindPosition | null => this.#position.get();
  readonly news = (): FindNews | null => this.#news.get();
  /** Whether a replace, replace-all or undo is under way (the panel's buttons are off meanwhile). */
  readonly busy = (): boolean => this.#busy.get();

  /** F1: opens the panel and searches for what it holds. */
  show(): void {
    if (this.#open.get()) return;
    this.#open.set(true);
    this.#want('all', 0);
  }

  /** F8: closes the panel: the highlights go, nothing more is searched. */
  hide(): void {
    if (!this.#open.get()) return;
    this.#open.set(false);
    this.#searches += 1;
    this.#cancelWanted();
    this.#options.place.editor()?.showMatches([]);
    this.#status.set({ kind: 'idle' });
    this.#position.set(null);
    this.#news.set(null);
  }

  /** F2: the query's text or an option changed. */
  setQuery(change: Partial<FindQuery>): void {
    this.#query.set({ ...this.#query.get(), ...change });
    this.#news.set(null);
    this.#want('all', SEARCH_DELAY_MS);
  }

  setReplacement(text: string): void {
    this.#replacement.set(text);
  }

  /** F2: the scope changed. */
  setScope(scope: FindScope): void {
    if (scope === this.#scope.get()) return;
    this.#scope.set(scope);
    this.#news.set(null);
    this.#want('all', 0);
  }

  /** F9: the open chapter's text changed. */
  edited(): void {
    if (this.#status.get().kind === 'found') this.#want('open', SEARCH_DELAY_MS);
  }

  /** F10: another chapter opened. */
  chapterOpened(): void {
    this.#want('open', 0);
  }

  /** F11: another book opened, or the book was read again. */
  bookChanged(): void {
    this.#want('all', 0);
  }

  /** F2: searches now, whatever was waiting (Enter in the query). */
  searchNow(): Promise<void> {
    this.#cancelWanted();
    return this.#search('all');
  }

  /**
   * F3: the next match, after every move or replace asked before it. The writer's own move ends the last replace's news,
   * so the place is what is said next (deep-fix r4).
   */
  next(): Promise<void> {
    return this.#act(() => {
      this.#news.set(null);
      return this.#move(1);
    });
  }

  /** F4: the match before, after every move or replace asked before it; as {@link next}, the news ends. */
  previous(): Promise<void> {
    return this.#act(() => {
      this.#news.set(null);
      return this.#move(-1);
    });
  }

  /** Runs `action` after every action asked before it has ended. */
  #act(action: () => Promise<void>): Promise<void> {
    const run = this.#actions.then(action);
    this.#actions = Promise.allSettled([run]);
    return run;
  }

  /**
   * F5 (principle 6): replaces the current match, when it is still selected, with the open chapter's text and the
   * match's range as they are now; the editor takes the answer only if it still holds that text. Then the next match. With
   * no current match it moves to the next, as Next does. After every move or replace asked before it.
   */
  replace(): Promise<void> {
    return this.#act(() => this.#replaceNow());
  }

  /** {@link replace}'s body. */
  async #replaceNow(): Promise<void> {
    const { place } = this.#options;
    const editor = place.editor();
    const chapterId = place.chapterId();
    const current = editor?.currentMatch ?? null;
    if (!place.editable()) return;
    if (chapterId === null || current === null || editor?.selection.from !== current.from || editor.selection.to !== current.to) {
      await this.#move(1);
      return;
    }
    this.#busy.set(true);
    this.#news.set(null);
    const text = editor.parsed.text;
    const outcome = await this.#options.run({ kind: 'replace', query: this.#query.get(), replacement: this.#replacement.get(), chapters: [{ id: chapterId, text }], only: { chapter: chapterId, ...current } });
    this.#busy.set(false);
    if (outcome.kind !== 'replaced') {
      this.#failed(outcome);
      return;
    }
    const [answer] = outcome.chapters;
    if (answer?.text !== null && answer !== undefined) {
      // Deep-fix r3: the move goes on from the replacement's end, which a text diff can't tell when the replacement holds
      // or equals the match.
      if (editor.replaceText(text, answer.text) !== null && answer.after !== null) editor.selectMatch({ from: answer.after, to: answer.after });
    } else if (answer?.left === 1) this.#news.set({ kind: 'left-one' });
    await this.#search('open');
    await this.#move(1);
  }

  /** F6, table L: replaces every match in scope through the workspace, then searches again; after every action before it. */
  replaceAll(): Promise<void> {
    return this.#act(() => this.#replaceAllNow());
  }

  /** {@link replaceAll}'s body. */
  async #replaceAllNow(): Promise<void> {
    const { place } = this.#options;
    if (!place.editable() || this.#query.get().text === '') return;
    this.#busy.set(true);
    this.#news.set({ kind: 'replacing' });
    const query = this.#query.get();
    const replacement = this.#replacement.get();
    const outcome = await place.replaceAll(this.#scope.get(), async (chapters) => {
      const answer = await this.#options.run({ kind: 'replace', query, replacement, chapters, only: null });
      return answer.kind === 'replaced' || answer.kind === 'refused' || answer.kind === 'stopped' ? answer : { kind: 'failed' };
    });
    this.#busy.set(false);
    this.#news.set({ kind: 'replaced-all', outcome });
    await this.#search('all');
  }

  /** F7, table U: the book-wide undo, then a new search; after every action before it. */
  undoReplaceAll(): Promise<void> {
    return this.#act(() => this.#undoNow());
  }

  /** {@link undoReplaceAll}'s body. */
  async #undoNow(): Promise<void> {
    const { place } = this.#options;
    if (!place.editable()) return;
    this.#busy.set(true);
    const outcome = await place.undoReplaceAll();
    this.#busy.set(false);
    this.#news.set({ kind: 'undone', outcome });
    await this.#search('all');
  }

  /** Asks for a search after `ms`, in place of any waiting; one for every chapter wins over one for the open chapter. */
  #want(asked: Wanted, ms: number): void {
    if (!this.#open.get()) return;
    const wanted = this.#pending?.wanted === 'all' ? 'all' : asked;
    this.#cancelWanted();
    this.#pending = {
      wanted,
      cancel: this.#options.schedule(ms, () => {
        this.#pending = null;
        void this.#search(wanted);
      }),
    };
  }

  #cancelWanted(): void {
    this.#pending?.cancel();
    this.#pending = null;
  }

  /**
   * Runs a search (T5: only the last one started counts): every chapter in scope (`all`), or the open chapter alone,
   * its count put into the results (`open`, F9, F10; a search of everything instead when there are none for this
   * book and scope). The open chapter's matches are shown when the editor still holds the text searched; otherwise it is
   * searched again. Showing matches leaves none current, so there is no position until the next move.
   */
  async #search(wanted: Wanted): Promise<void> {
    const { place } = this.#options;
    const query = this.#query.get();
    const scope = this.#scope.get();
    const bookId = place.bookId();
    const before = this.#status.get();
    // The results an open-chapter search updates: only those of this book and scope that hold the open chapter (deep-fix
    // r1: with scope "this chapter", another chapter opened is a new search, not an update of the last one's count).
    const chapterId = place.chapterId();
    const partial =
      wanted === 'open' && before.kind === 'found' && before.results.bookId === bookId && before.results.scope === scope && before.results.chapters.some((id) => id === chapterId)
        ? before.results
        : null;
    const id = ++this.#searches;
    this.#position.set(null);
    if (!this.#open.get() || query.text === '' || bookId === null) {
      place.editor()?.showMatches([]);
      this.#status.set({ kind: 'idle' });
      return;
    }
    if (partial === null) this.#status.set({ kind: 'searching' });
    const texts = await place.texts(partial === null ? scope : 'chapter');
    if (id !== this.#searches) return;
    if (texts === null) {
      this.#status.set({ kind: 'idle' });
      return;
    }
    const open = texts.chapters.find((chapter) => chapter.id === place.chapterId()) ?? null;
    const outcome = await this.#options.run({ kind: 'search', query, chapters: texts.chapters, ranges: open?.id ?? null });
    if (id !== this.#searches || outcome.kind === 'superseded') return;
    if (outcome.kind !== 'found') {
      this.#failed(outcome);
      return;
    }
    const editor = place.editor();
    if (open !== null && editor !== null && place.chapterId() === open.id) {
      if (editor.parsed.text === open.text) editor.showMatches(outcome.ranges);
      else this.#want('open', 0);
    }
    if (partial === null) {
      this.#status.set({ kind: 'found', results: { bookId, scope, chapters: texts.chapters.map((chapter) => chapter.id), counts: outcome.counts, unreadable: texts.unreadable } });
    } else if (open !== null) {
      this.#status.set({ kind: 'found', results: { ...partial, counts: partial.chapters.map((chapter, i) => Number(chapter === open.id ? outcome.counts[0] : partial.counts[i])) } });
    }
  }

  /** T1, T4, Q3, Q7: a job that gave no answer it asked for: said, and no highlights. Any other answer is a failure. */
  #failed(outcome: FindOutcome): void {
    this.#options.place.editor()?.showMatches([]);
    this.#position.set(null);
    this.#status.set(outcome.kind === 'refused' ? { kind: 'refused', problem: outcome.problem } : outcome.kind === 'stopped' ? { kind: 'stopped' } : { kind: 'failed' });
  }

  /**
   * F3, F4, F14: the next (or previous) match after the selection in the open chapter; past the last, the first (or
   * last) of the next chapter in scope that has one, opened, round to the first; with scope "this chapter", round to
   * the chapter's first. A search runs first when there are no results.
   */
  async #move(step: 1 | -1): Promise<void> {
    const { place } = this.#options;
    if (!this.#open.get()) return;
    if (this.#status.get().kind !== 'found' || this.#pending !== null) await this.searchNow();
    const status = this.#status.get();
    if (status.kind !== 'found') return;
    const editor = place.editor();
    const chapterId = place.chapterId();
    if (editor !== null && this.#select(editor, step, false)) return;
    // F3: scope "this chapter" goes round the chapter, never into another (deep-fix r1).
    if (status.results.scope === 'chapter') {
      if (editor !== null) this.#select(editor, step, true);
      return;
    }
    const { chapters, counts } = status.results;
    // The chapters in the order the move meets them: from the one after (or before) the open one, round to it.
    const ordered = chapters.map((id, i) => ({ id, count: Number(counts[i]) }));
    if (step === -1) ordered.reverse();
    const here = chapterId === null ? -1 : ordered.findIndex((chapter) => chapter.id === chapterId);
    for (const { id: target, count } of [...ordered.slice(here + 1), ...ordered.slice(0, here + 1)]) {
      if (count === 0) continue;
      if (target === chapterId && editor !== null) {
        this.#select(editor, step, true);
        return;
      }
      await place.openChapter(target);
      if (place.chapterId() !== target) return;
      // F10's own search of the chapter just opened is this one.
      this.#cancelWanted();
      await this.#search('open');
      const opened = place.editor();
      if (opened !== null) this.#select(opened, step, true);
      return;
    }
  }

  /**
   * Selects the match after the selection (`step` 1) or before it (−1) in `editor`; with `round`, the first (or last)
   * when there is none. False when nothing was selected.
   */
  #select(editor: EditorHandle, step: 1 | -1, round: boolean): boolean {
    const ranges = editor.matches;
    const { from, to } = editor.selection;
    let pick: number | null = null;
    for (let i = 0; i < ranges.length; i += 2) {
      const start = Number(ranges[i]);
      const end = Number(ranges[i + 1]);
      if (step === 1 && start >= to) {
        pick = i;
        break;
      }
      if (step === -1 && end <= from) pick = i;
    }
    if (pick === null && round && ranges.length > 0) pick = step === 1 ? 0 : ranges.length - 2;
    if (pick === null) return false;
    editor.selectMatch({ from: Number(ranges[pick]), to: Number(ranges[pick + 1]) });
    this.#position.set({ index: pick / 2 + 1, of: ranges.length / 2 });
    return true;
  }
}
